import dayjs, { type Dayjs } from 'dayjs'

// types
import type {
  CalendarDayCell,
  CalendarRange,
  CalendarTimeSlot,
  CalendarViewType,
  CalendarWeekStart
} from '@/types/components/calendar'

// utilities
import { isWeekend, startOfWeek } from '@/utilities/calendar/datetime'
import { formatSlotLabel } from '@/utilities/calendar/format'

/**
 * Builds a single day cell.
 * @param date - Day the cell represents
 * @param displayedMonth - Month currently on screen, used for the muted styling of adjacent days
 * @param today - Injected so tests are not clock-dependent
 */
function buildDayCell(date: Dayjs, displayedMonth: Dayjs | null, today: Dayjs): CalendarDayCell {
  return {
    date,
    key: date.format('YYYY-MM-DD'),
    dayNumber: date.date(),
    isCurrentMonth: displayedMonth === null || date.month() === displayedMonth.month(),
    isToday: date.isSame(today, 'day'),
    isWeekend: isWeekend(date)
  }
}

/**
 * Builds the month grid as rows of seven days.
 * The row count varies between four and six so cells get as much height as possible.
 * @param cursor - Any date within the month to display
 * @param weekStart - 0 = Sunday through 6 = Saturday
 * @param today - Injected so tests are not clock-dependent
 */
export function buildMonthGrid(
  cursor: Dayjs,
  weekStart: CalendarWeekStart = 1,
  today: Dayjs = dayjs()
): CalendarDayCell[][] {
  const monthStart = cursor.startOf('month')
  const gridStart = startOfWeek(monthStart, weekStart)
  const gridEnd = startOfWeek(cursor.endOf('month'), weekStart).add(7, 'day')
  const weekCount = Math.round(gridEnd.diff(gridStart, 'day') / 7)

  return Array.from({ length: weekCount }, (_, weekIndex) =>
    Array.from({ length: 7 }, (_, dayIndex) =>
      buildDayCell(gridStart.add(weekIndex * 7 + dayIndex, 'day'), monthStart, today)
    )
  )
}

/**
 * Builds the seven day columns of the week view.
 * @param cursor - Any date within the week to display
 * @param weekStart - 0 = Sunday through 6 = Saturday
 * @param today - Injected so tests are not clock-dependent
 */
export function buildWeekDays(
  cursor: Dayjs,
  weekStart: CalendarWeekStart = 1,
  today: Dayjs = dayjs()
): CalendarDayCell[] {
  const start = startOfWeek(cursor, weekStart)

  return Array.from({ length: 7 }, (_, index) => buildDayCell(start.add(index, 'day'), null, today))
}

/**
 * Builds the single day column of the day view.
 * @param cursor - Day to display
 * @param today - Injected so tests are not clock-dependent
 */
export function buildDayColumn(cursor: Dayjs, today: Dayjs = dayjs()): CalendarDayCell[] {
  return [buildDayCell(cursor.startOf('day'), null, today)]
}

/**
 * Builds the day columns for whichever view is active.
 * Month view returns a flat list of every cell in its grid.
 * @param cursor - Date anchoring the visible period
 * @param view - Active view
 * @param weekStart - 0 = Sunday through 6 = Saturday
 * @param today - Injected so tests are not clock-dependent
 */
export function buildViewDays(
  cursor: Dayjs,
  view: CalendarViewType,
  weekStart: CalendarWeekStart = 1,
  today: Dayjs = dayjs()
): CalendarDayCell[] {
  // Month view flattens its rows so callers can iterate every visible day
  if (view === 'month') {
    return buildMonthGrid(cursor, weekStart, today).flat()
  }

  // Week view spans the seven days around the cursor
  if (view === 'week') {
    return buildWeekDays(cursor, weekStart, today)
  }

  return buildDayColumn(cursor, today)
}

/**
 * Builds the horizontal lines of the time-grid axis.
 * @param dayStartHour - First hour shown, 0-23
 * @param dayEndHour - Last hour shown, 1-24 and greater than `dayStartHour`
 * @param slotMinutes - Spacing between lines
 * @param hour12 - Use 12-hour labels
 */
export function buildTimeSlots(
  dayStartHour: number,
  dayEndHour: number,
  slotMinutes: number,
  hour12 = false
): CalendarTimeSlot[] {
  const startMinute = Math.max(0, Math.round(dayStartHour * 60))
  const endMinute = Math.min(24 * 60, Math.round(dayEndHour * 60))
  const step = Math.max(1, Math.round(slotMinutes))

  // An inverted or zero-length window has no slots to draw
  if (endMinute <= startMinute) {
    return []
  }

  const slots: CalendarTimeSlot[] = []

  for (let minute = startMinute; minute < endMinute; minute += step) {
    const isHour = minute % 60 === 0

    slots.push({
      minute,
      // Only whole hours are labelled, so sub-hour slots stay uncluttered
      label: isHour ? formatSlotLabel(minute, hour12) : '',
      isHour
    })
  }

  return slots
}

/**
 * The date range currently on screen, as an inclusive start and exclusive end.
 * Month view includes the leading and trailing days of adjacent months.
 * @param cursor - Date anchoring the visible period
 * @param view - Active view
 * @param weekStart - 0 = Sunday through 6 = Saturday
 */
export function getVisibleRange(
  cursor: Dayjs,
  view: CalendarViewType,
  weekStart: CalendarWeekStart = 1
): CalendarRange {
  // Month view fetches the padded grid, not just the calendar month
  if (view === 'month') {
    const gridStart = startOfWeek(cursor.startOf('month'), weekStart)
    const gridEnd = startOfWeek(cursor.endOf('month'), weekStart).add(7, 'day')

    return { start: gridStart.toISOString(), end: gridEnd.toISOString() }
  }

  // Week view spans exactly seven days from the configured first day
  if (view === 'week') {
    const weekStartDate = startOfWeek(cursor, weekStart)

    return { start: weekStartDate.toISOString(), end: weekStartDate.add(7, 'day').toISOString() }
  }

  const dayStart = cursor.startOf('day')

  return { start: dayStart.toISOString(), end: dayStart.add(1, 'day').toISOString() }
}

/**
 * Moves the cursor by one period in the active view.
 * @param cursor - Current cursor
 * @param view - Active view
 * @param direction - 1 for forwards, -1 for backwards
 */
export function shiftCursor(cursor: Dayjs, view: CalendarViewType, direction: 1 | -1): Dayjs {
  // Month steps keep the cursor on the first of the month to avoid 31st-to-30th drift
  if (view === 'month') {
    return cursor.startOf('month').add(direction, 'month')
  }

  return cursor.add(direction, view === 'week' ? 'week' : 'day')
}
